import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { ArrowLeft, Send, Bell } from "lucide-react";

export default function AdminNotifications() {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [audience, setAudience] = useState("all_businesses");
  const [sendEmail, setSendEmail] = useState(false);
  const [sending, setSending] = useState(false);

  const AUD = [
    { v: "all_businesses", label: "Negocios" },
    { v: "all_clients", label: "Clientes" },
    { v: "all", label: "Todos" },
  ];

  const send = async () => {
    if (!subject.trim() || !message.trim()) { toast.error("Completa asunto y mensaje"); return; }
    const who = audience === "all_clients" ? "TODOS los clientes" : audience === "all" ? "TODOS los negocios y clientes" : "TODOS los negocios";
    if (!window.confirm(`¿Enviar esta notificación a ${who}? Esta acción no se puede deshacer.`)) return;
    setSending(true);
    try {
      const { data } = await api.post("/admin/notifications", { audience, subject, message, send_email: sendEmail });
      toast.success(`Enviado · negocios: ${data.sent_businesses || 0}, clientes: ${data.sent_clients || 0}`);
      setSubject(""); setMessage("");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setSending(false); }
  };

  return (
    <div className="mx-auto max-w-2xl px-6 py-10" data-testid="admin-notifications-page">
      <a href="/admin" className="mb-4 inline-flex items-center gap-1 text-sm font-semibold text-slate-500 hover:text-primary"><ArrowLeft className="h-4 w-4" /> Volver</a>
      <h1 className="flex items-center gap-2 font-head text-2xl font-extrabold tracking-tight text-secondary"><Bell className="h-6 w-6 text-primary" /> Enviar notificación a negocios</h1>
      <p className="mt-1 text-sm text-slate-500">Se mostrará en el panel de cada negocio (campanita) y, opcionalmente, se envía por correo.</p>

      <div className="mt-6 space-y-4 rounded-sm border border-slate-200 bg-white p-6">
        <div>
          <Label className="text-xs font-bold uppercase text-slate-500">Destinatarios</Label>
          <div className="mt-2 flex gap-2" data-testid="audience-selector">
            {AUD.map((a) => (
              <button key={a.v} type="button" onClick={() => setAudience(a.v)} data-testid={`audience-${a.v}`}
                className={`rounded-sm border px-4 py-2 text-sm font-semibold transition-colors ${audience === a.v ? "border-primary bg-primary text-white" : "border-slate-200 text-slate-600 hover:border-primary"}`}>
                {a.label}
              </button>
            ))}
          </div>
          {audience !== "all_businesses" && <p className="mt-2 text-xs text-slate-400">Los clientes reciben la notificación por correo (no tienen panel en la app).</p>}
        </div>
        <div>
          <Label className="text-xs font-bold uppercase text-slate-500">Asunto</Label>
          <Input value={subject} onChange={(e) => setSubject(e.target.value)} className="mt-1 rounded-sm" data-testid="notif-subject" placeholder="Mantenimiento programado" />
        </div>
        <div>
          <Label className="text-xs font-bold uppercase text-slate-500">Mensaje</Label>
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1 rounded-sm" rows={5} data-testid="notif-message" placeholder="Escribe el mensaje para los negocios…" />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600"><Switch checked={sendEmail} onCheckedChange={setSendEmail} data-testid="notif-email-toggle" /> También enviar por correo (Resend)</label>
        <Button onClick={send} disabled={sending} className="rounded-sm bg-primary hover:bg-orange-600" data-testid="notif-send-btn"><Send className="mr-2 h-4 w-4" />{sending ? "Enviando…" : (audience === "all_clients" ? "Enviar a todos los clientes" : audience === "all" ? "Enviar a todos" : "Enviar a todos los negocios")}</Button>
      </div>
    </div>
  );
}
